import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { window } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";
import { classNames } from "@swa-ui/string";

import { Button } from "../Button";
import { Icon } from "../Icon";
import { Text } from "../Text";
import styles from "./CalendarSecondary.module.scss";
import { Month } from "./Month";

const MIN_WIDTH_FOR_ICONS = 360;

/**
 * Component to display calendar months. Currently not intended to be used outside of DateSelector.
 */
export const CalendarSecondary = (props) => {
  const {
    className,
    dateEnd,
    dateStart,
    daysOfWeekInitials = "SMTWTFS",
    departText,
    firstBookableDate,
    focusField,
    lastBookableDate,
    numberDates,
    onClick,
    returnText,
    revealed,
    selectDateText,
  } = props;
  const [currentFocusField, setCurrentFocusField] = useState(focusField);
  const [hoverDate, setHoverDate] = useState();
  const [offsetTop, setOffsetTop] = useState(0);
  const containerRef = useRef({});
  const endMarker = useRef({});
  const monthsDisplayed = getMonthsToDisplay();
  const monthRefs = [...Array(monthsDisplayed)].map(() => useRef());
  const monthOffsets = useRef([]);
  const shouldShowIcons = useMemo(
    () => window.innerWidth > MIN_WIDTH_FOR_ICONS,
    [window.innerWidth]
  );

  useEffect(() => {
    for (let ndx = 0; ndx < monthsDisplayed; ndx += 1) {
      monthOffsets.current[ndx] = monthRefs[ndx].current.offsetTop;
    }
  }, [monthRefs[0]?.current]);

  useEffect(() => {
    const { overflow } = window.document.body.style;

    if (revealed) {
      window.document.body.style.overflow = "hidden";
    }

    return () => {
      window.document.body.style.overflow = overflow || "";
    };
  }, [revealed]);

  useEffect(() => {
    if (containerRef.current?.offsetTop !== 0) {
      setOffsetTop(containerRef.current.offsetTop);

      const heightOfCalendarContent = endMarker.current.offsetTop;
      const heightOfViewport = window.innerHeight - offsetTop;
      const selectedMonth = getMonth(true);
      const top = Math.min(
        heightOfCalendarContent - heightOfViewport,
        monthOffsets.current[selectedMonth]
      );

      containerRef.current.scrollTo({ behavior: "instant", left: 0, top });
    }
  }, [containerRef.current?.offsetTop, revealed]);

  useEffect(() => {
    const selectedMonth = getMonth(true);

    containerRef.current?.scrollTo?.({
      behavior: "instant",
      left: 0,
      top: monthOffsets.current[selectedMonth],
    });
  }, [dateEnd, dateStart]);

  return (
    <>
      {renderStartEndButtons()}
      <div {...getProps()}>
        {[...Array(monthsDisplayed)].map((_, index) => (
          <Month key={index} {...getMonthProps(index)} />
        ))}
        <div className={styles.endMarker} ref={endMarker}>
          &nbsp;
        </div>
      </div>
    </>
  );

  function renderStartEndButtons() {
    return (
      <div className={styles.fieldOptions}>
        {renderButton("start")}
        {numberDates === 2 && renderButton("end")}
      </div>
    );
  }

  function renderButton(field) {
    const isStart = field === "start";

    return (
      <Button {...getButtonProps(field)}>
        {shouldShowIcons && <Icon {...getIconProps(field)} />}
        <div className={styles.buttonContent}>
          <Text {...getButtonTextProps()}>{isStart ? departText : returnText}</Text>
          <Text bold size="xlarge">
            {getNormalizedDate(isStart ? dateStart : dateEnd)}
          </Text>
        </div>
      </Button>
    );
  }

  function getProps() {
    return {
      className: classNames(className, styles.calendar),
      ref: containerRef,
    };
  }

  function getButtonProps(field) {
    return {
      className: getButtonClassName(field),
      onClick: () => setCurrentFocusField(field),
      styleType: "no-style",
    };
  }

  function getIconProps(field) {
    return field === "start" ? getStartIconProps() : getEndIconProps();
  }

  function getStartIconProps() {
    // TODO tokenize all new color values PHX-2488
    return {
      background: { color: "primary-200", type: "circle" },
      color: "primary-900",
      name: "Airplane",
      shrink: 0.75,
      size: "size30",
    };
  }

  function getEndIconProps() {
    // TODO tokenize all new color values PHX-2488
    return {
      actions: ["rotate270"],
      background: { color: "success-600", type: "circle" },
      className: styles.icon,
      color: "primary-900",
      initial: ["rotate270"],
      name: "Airplane",
      shrink: 0.75,
      size: "size30",
    };
  }

  function getButtonTextProps() {
    return {
      bold: true,
      className: styles.label,
      color: "neutral-300",
      size: "small",
      textType: "label",
    };
  }

  function getMonthProps(calendarIndex) {
    const { month, year } = getSubsequentMonthYear(calendarIndex);

    return {
      className: getMonthClass(calendarIndex),
      dateEnd,
      dateStart,
      daysOfWeekInitials,
      firstBookableDate,
      focusField: currentFocusField,
      hoverDate,
      lastBookableDate: getLastBookableDateString(),
      month,
      numberDates,
      onClick: handleClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ref: monthRefs[calendarIndex],
      year,
    };
  }

  function getMonthClass(calendarIndex) {
    return classNames(styles.month, {
      [styles.separator]: calendarIndex < monthsDisplayed - 1,
    });
  }

  function getButtonClassName(button) {
    return classNames({
      [styles.buttonEndDate]: button === "end",
      [styles.buttonStartDate]: button === "start",
      [styles.selected]: button === currentFocusField,
    });
  }

  function handleClick(date) {
    if (currentFocusField === "start") {
      setCurrentFocusField("end");
    }
    onClick?.(date, currentFocusField);
  }

  function handleMouseEnter(date) {
    setHoverDate(date);
  }

  function handleMouseLeave() {
    setHoverDate(undefined);
  }

  function getNormalizedDate(workingDate) {
    return !workingDate || workingDate === "//" ? selectDateText : workingDate;
  }

  function getMonthsToDisplay() {
    const lastDate = getEndingDate();
    const firstDate = getStartingDate();
    let months = (lastDate.year() - firstDate.year()) * 12;

    months -= firstDate.month();
    months += lastDate.month();

    return months + 1;
  }

  function getStartingDate() {
    return firstBookableDate ? swaDate(firstBookableDate) : swaDate();
  }

  function getEndingDate() {
    const lastBookableDateString = getLastBookableDateString();

    return lastBookableDateString ? swaDate(lastBookableDateString) : swaDate();
  }

  function getLastBookableDateString() {
    let lastBookableDateString = lastBookableDate;

    if (!lastBookableDateString) {
      const startingDate = getStartingDate();
      const oneYearAfterStartingDate = startingDate.add(1, "year");

      lastBookableDateString = oneYearAfterStartingDate.format("M/D/YYYY, h:mm:ss A");
    }

    return lastBookableDateString;
  }

  function getMonth(startDatePriority) {
    const date = currentFocusField === "end" ? dateEnd : dateStart;
    const startingMonth = getStartingDate().month() + 1;
    let month = startingMonth;

    if (currentFocusField === "end" && dateStart) {
      if (parseInt(dateStart.split("/")[0]) < month) {
        month -= 1;
      }
    } else if (date && date !== "//") {
      month = parseInt(date.split("/")[0]);
    }

    const monthDifference = month - startingMonth;
    let result = month > startingMonth ? monthDifference : monthDifference + 12;

    if (startDatePriority && monthDifference === 0) {
      result = parseInt(dateStart.split("/")[0]) - startingMonth;
    }

    return result;
  }

  function getSubsequentMonthYear(index) {
    let startingDate = getStartingDate();
    const startingMonth = startingDate.month();
    const startingYear = startingDate.year();

    startingDate = swaDate(`${startingMonth + 1}/1/${startingYear}`).month(startingMonth + index);

    return {
      month: `${startingDate.month() + 1}`,
      year: `${startingDate.year()}`,
    };
  }
};

CalendarSecondary.displayName = "CalendarSecondary";

CalendarSecondary.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** End date if given should be in this format: MM/DD/YYYY (02/02/2020). */
  dateEnd: PropTypes.string,

  /** Start date if given should be in this format: MM/DD/YYYY (02/02/2020). */
  dateStart: PropTypes.string,

  /** Initial letters of each day of the week: Sunday, Monday. */
  daysOfWeekInitials: PropTypes.string,

  /** Text to display in the depart date button for mobile view. Typically: "Depart". */
  departText: PropTypes.string.isRequired,

  /** First date that can be booked. Should be in this format: MM/DD/YYYY. */
  firstBookableDate: PropTypes.string,

  /** Indicates which date the traveler is selecting. */
  focusField: PropTypes.oneOf(["end", "start"]),

  /** Last date that can be booked. Should be in this format: MM/DD/YYYY. */
  lastBookableDate: PropTypes.string,

  /** One or two dates can be selected so the calendar will allow for start and end dates. */
  numberDates: PropTypes.number,

  /** Event handler to learn when an end date is selected. The event handler will be passed a date string. */
  onClick: PropTypes.func,

  /** Text to display in the return date button for mobile view. Typically: "RETURN". */
  returnText: PropTypes.string.isRequired,

  /** Indicator that the calendar should be rendered. */
  revealed: PropTypes.bool,

  /** Text to display when no date is selected. */
  selectDateText: PropTypes.string.isRequired,
};
